import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { projectKeys } from '@/features/projects/api'
import { api } from '@/lib/api/client'

export const UPDATE_PUSH_PERM = 'installations.update.push'
export const RELEASE_WRITE_PERM = 'releases.write'
export const INSTALLATION_MANAGE_PERM = 'installations.manage'
export const CATALOG_WRITE_PERM = 'catalog:module:write'

/** Kurulum ajanının iş durumları (klinik ADR-0024). */
export const UPDATE_STATUSES = [
  'scheduled',
  'running',
  'succeeded',
  'failed',
  'awaiting_restore',
  'restore_requested',
  'restoring',
  'restored',
  'cancelled',
] as const

export type UpdatePush = {
  id: string
  installation_id: string
  customer_server_id: string
  reason: string
  status: 'requested' | 'approved' | 'rejected' | 'running' | 'succeeded' | 'failed'
  requested_by: string
  decided_by: string
  output: string
  created_at: string
  decided_at?: string | null
  started_at?: string | null
  finished_at?: string | null
}

export type CustomerServer = {
  id: string
  account_id: string
  name: string
  host: string
  ip_address: string
  has_access: boolean
  ssh_user: string
  ssh_port: number
  ssh_host_key_fingerprint: string
  has_ssh_key: boolean
}

export type CatalogSyncResult = {
  product: string
  version?: string
  applied: boolean
  plan: {
    add_modules: { code: string; is_core: boolean; requires: string[] }[] | null
    update_modules: { code: string; is_core: boolean; requires: string[] }[] | null
    deactivate_modules: string[] | null
    add_features: { code: string; module: string }[] | null
    update_features: { code: string; module: string }[] | null
    deactivate_features: string[] | null
  }
}

const invalidateInstallation = (qc: ReturnType<typeof useQueryClient>, id: string) => {
  void qc.invalidateQueries({ queryKey: ['installations', id] })
  void qc.invalidateQueries({ queryKey: ['update-pushes', id] })
}

export function usePublishRelease() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      version: string
      notes: string
      security: boolean
      min_panel_version: string
    }) =>
      api.put(`/api/v1/releases/${id}/publish`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['releases'] }),
  })
}

export function useCreateImageJob(installationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (notes: string) =>
      api.post<{ id: string }>(`/api/v1/installations/${installationId}/releases`, { notes }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['releases'] }),
  })
}

export function useAssignImage(installationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (releaseId: string) =>
      api.put(`/api/v1/installations/${installationId}/image`, { release_id: releaseId }),
    onSuccess: () => invalidateInstallation(qc, installationId),
  })
}

export function useLinkServer(installationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (serverId: string) =>
      api.put(`/api/v1/installations/${installationId}`, { customer_server_id: serverId }),
    onSuccess: () => invalidateInstallation(qc, installationId),
  })
}

export const useServers = (accountId: string) =>
  useQuery({
    queryKey: ['customer-servers', accountId],
    queryFn: () => api.get<CustomerServer[]>('/api/v1/customer-servers', { account_id: accountId }),
    enabled: !!accountId,
  })

export function useSetServerSSH() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      ssh_user: string
      ssh_port: number
      host_key_fingerprint: string
      private_key: string
    }) => api.put(`/api/v1/customer-servers/${id}/ssh`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customer-servers'] }),
  })
}

export const useUpdatePushes = (installationId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['update-pushes', installationId],
    queryFn: () =>
      api.get<{ items: UpdatePush[] }>(`/api/v1/installations/${installationId}/update-pushes`).then((r) => r.items),
    enabled: enabled && !!installationId,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((p) => p.status === 'approved' || p.status === 'running') ? 10_000 : false,
  })

export function useRequestPush(installationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<{ id: string }>(`/api/v1/installations/${installationId}/update-pushes`, { reason }),
    onSuccess: () => invalidateInstallation(qc, installationId),
  })
}

export function useDecidePush(installationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api.post<UpdatePush>(`/api/v1/update-pushes/${id}/${approve ? 'approve' : 'reject'}`),
    onSuccess: () => invalidateInstallation(qc, installationId),
  })
}

export function useUnlockVault() {
  return useMutation({ mutationFn: (pin: string) => api.post('/api/v1/vault/unlock', { pin }) })
}

export function useCatalogSync() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ product, doc, dryRun }: { product: string; doc: unknown; dryRun: boolean }) =>
      api.post<CatalogSyncResult>(`/api/v1/catalog/products/${product}/sync${dryRun ? '?dry_run=true' : ''}`, doc),
    onSuccess: (r) => {
      if (r.applied) void qc.invalidateQueries({ queryKey: projectKeys.catalog })
    },
  })
}
